/**
 * Vue jour (planning plus étroit que GRID_MIN_WIDTH, index.tsx : téléphone, tablette, fenêtre non maximisée ; SPEC PL2
 * et § 2.4 point 1) : sélecteur de jour (7 pastilles, aujourd'hui par défaut, jours passés atténués, jours sans cours
 * signalés, `aria-pressed`) puis les cours du seul jour choisi, et un bouton « Jour suivant » — pas la liste des 7
 * jours empilés de la maquette. Au-delà de 640 px, colonne de lecture bornée (planning.css).
 */
import { addDays, format, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLayoutEffect, useState, type ReactNode, useRef } from 'react';
import type { PlanningClass } from '../../api/planning';
import { revealElements } from '../../lib/planning/skeleton';
import { dayLong, dayShort, isPastDay } from '../../lib/planning/week';
import { IconRight } from './icons';
import { SkeletonList } from './Skeleton';

interface DayViewProps {
  days: Date[];
  today: Date;
  selectedDay: Date;
  classesByDay: PlanningClass[][];
  headingLevel: 2 | 3;
  renderCard: (c: PlanningClass) => ReactNode;
  isLoading: boolean;
  onSelectDay: (day: Date) => void;
  /** Dimanche → lundi de la semaine suivante. */
  onNextDay: () => void;
  emptyDay: ReactNode;
  /** Cours déjà commencé (en cours ou terminé), hors cours réservé par le client : repliés en tête du jour courant. */
  isStarted?: (c: PlanningClass) => boolean;
  /** Vue multi-marques : les cartes en attente portent le bloc de l'étiquette de marque. */
  skeletonBrandTag: boolean;
  /** Montée à la place d'un squelette (celui du serveur) : les cartes apparaissent en fondu, comme après un chargement. */
  revealOnMount?: boolean;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export function DayView({
  days,
  today,
  selectedDay,
  classesByDay,
  headingLevel,
  renderCard,
  isLoading,
  onSelectDay,
  onNextDay,
  emptyDay,
  isStarted,
  skeletonBrandTag,
  revealOnMount = false,
}: DayViewProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const index = Math.max(
    0,
    days.findIndex((day) => isSameDay(day, selectedDay)),
  );
  const classes = classesByDay[index] ?? [];
  const nextDay = addDays(selectedDay, 1);
  const rootRef = useRef<HTMLDivElement>(null);
  const wasLoading = useRef(isLoading || revealOnMount);
  // Cartes (et « Jour suivant ») arrivées à la place des cartes en attente : fondu léger, sélecteur de jour immobile.
  useLayoutEffect(() => {
    if (wasLoading.current && !isLoading) {
      revealElements(
        rootRef.current?.querySelectorAll(
          '.mmv2-dayview__list, .mmv2-dayview__finished, .mmv2-dayview__empty, .mmv2-dayview__next',
        ) ?? [],
      );
    }
    wasLoading.current = isLoading;
  }, [isLoading]);

  /*
   * Jour courant : les cours déjà commencés (terminés et en cours, inertes) sont repliés derrière « Afficher les 37 cours
   * déjà commencés », en tête de liste ; le cours réservé par le client reste visible. Revue du 05/10 : à 12:30, 37 cartes
   * inertes (3,4 écrans) avant le premier cours réservable sur téléphone, 60 à 18:00 (vue multi-marques) ; les seuls
   * « En cours » de 12:00 et 12:15 en faisaient encore 20 (2 écrans). Repli refermé à chaque jour.
   */
  const [openedDay, setOpenedDay] = useState<string | null>(null);
  const dayKey = selectedDay.toDateString();
  const isTodaySelected = isSameDay(selectedDay, today);
  const finished = isTodaySelected && isStarted ? classes.filter(isStarted) : [];
  const folded = finished.length > 0 && openedDay !== dayKey;
  const shown = folded ? classes.filter((c) => !finished.includes(c)) : classes;
  const finishedToggle =
    finished.length > 0 ? (
      <button
        type="button"
        className="mmv2-dayview__finished"
        aria-expanded={!folded}
        onClick={() => setOpenedDay(folded ? dayKey : null)}
      >
        {folded
          ? `Afficher ${finished.length > 1 ? `les ${finished.length} cours déjà commencés` : 'le cours déjà commencé'}`
          : `Masquer ${finished.length > 1 ? 'les cours déjà commencés' : 'le cours déjà commencé'}`}
      </button>
    ) : null;

  /**
   * Le jour choisi s'affiche en haut : sélecteur de jour (collant) puis sa liste. Pastilles collantes, donc touchées au
   * milieu d'une longue liste : sans ce défilement, le nouveau jour s'ouvrait à la même hauteur, sur ses cours de midi
   * (revue du 05/10). Seulement si le haut de la vue est passé sous l'en-tête collant (scroll-margin-top).
   */
  const showDayTop = () => {
    const root = rootRef.current;
    if (!root) return;
    const margin = parseFloat(getComputedStyle(root).scrollMarginTop) || 0;
    if (root.getBoundingClientRect().top < margin - 1) root.scrollIntoView({ block: 'start' });
  };

  return (
    <div className="mmv2-dayview" ref={rootRef}>
      <div className="mmv2-daypicker" role="group" aria-label="Jour">
        {days.map((day, dayIndex) => {
          const isToday = isSameDay(day, today);
          const isEmpty = !isLoading && (classesByDay[dayIndex]?.length ?? 0) === 0;
          return (
            <button
              key={day.toISOString()}
              type="button"
              className="mmv2-daypicker__day"
              aria-pressed={isSameDay(day, selectedDay)}
              aria-label={`${dayLong(day)}${isToday ? ', aujourd’hui' : ''}${isEmpty ? ', aucun cours' : ''}`}
              data-today={isToday || undefined}
              data-past={isPastDay(day, today) || undefined}
              data-empty={isEmpty || undefined}
              onClick={() => {
                onSelectDay(day);
                showDayTop();
              }}
            >
              <span className="mmv2-daypicker__name">{dayShort(day)}</span>
              <span className="mmv2-daypicker__num">{format(day, 'd')}</span>
            </button>
          );
        })}
      </div>

      <section className="mmv2-dayview__day" aria-busy={isLoading || undefined}>
        <Heading className="mmv2-dayview__title">{capitalize(dayLong(selectedDay))}</Heading>
        {isLoading ? (
          <SkeletonList className="mmv2-dayview__list" day={null} brandTag={skeletonBrandTag} />
        ) : classes.length ? (
          <>
            {finishedToggle}
            {shown.length ? (
              <ul className="mmv2-dayview__list">
                {shown.map((c) => (
                  <li key={`${c.SiteId}:${c.Id}`} className="mmv2-day__item">
                    {renderCard(c)}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          emptyDay
        )}
      </section>

      {!isLoading ? (
        <button
          type="button"
          className="mmv2-btn mmv2-btn--ghost mmv2-btn--block mmv2-dayview__next"
          onClick={() => {
            onNextDay();
            // Le jour suivant s'affiche en haut : sélecteur de jour (collant) puis sa liste.
            showDayTop();
          }}
        >
          Jour suivant · {capitalize(format(nextDay, 'EEEE d MMM', { locale: fr }))}
          <IconRight />
        </button>
      ) : null}
    </div>
  );
}
